<script setup lang="ts">
import InteractiveTopics from "~/components/InteractiveTopics.vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import { chapterHubByUnit, chapterHubRelatedLinks, getChapterHubFaqs, getChapterHubSchemaLinks } from "~/features/computer/data/chapter-hubs";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { computerMcqChapterByUnit } from "~/features/computer/data/mcq-registry";
import type { ComputerMcqUnit } from "~/features/computer/types/computer";

const props = defineProps<{
  unit: ComputerMcqUnit;
}>();

const config = chapterHubByUnit[props.unit];
const mcqChapter = computerMcqChapterByUnit[props.unit];
const shortQuestionsCard = config.practiceCards.find((item) => item.title === "Short Questions");
const longQuestionsCard = config.practiceCards.find((item) => item.title === "Long Questions");
const exerciseCard = config.practiceCards.find((item) => item.title === "Quick Exercise");
const faqs = getChapterHubFaqs(props.unit);

const keyTopicLinks = [
  { title: "Concepts", to: config.keyTopicsConceptTo },
  { title: "MCQs", to: mcqChapter.routes.mcqTo },
  ...(shortQuestionsCard ? [{ title: shortQuestionsCard.title, to: shortQuestionsCard.to }] : []),
  ...(longQuestionsCard ? [{ title: longQuestionsCard.title, to: longQuestionsCard.to }] : []),
  ...(exerciseCard ? [{ title: "Practice", to: exerciseCard.to }] : []),
];

usePageSeo({
  title: `${config.heroTitle} | Ustaad`,
  description: config.seoText,
  keywords: `chapter ${config.chapterNumber} ${config.chapterName.toLowerCase()}, 11th class computer ${config.chapterName.toLowerCase()}, first year computer chapter ${config.chapterNumber}, chapter ${config.chapterNumber} ${config.chapterName.toLowerCase()} mcqs, chapter ${config.chapterNumber} ${config.chapterName.toLowerCase()} short questions, chapter ${config.chapterNumber} ${config.chapterName.toLowerCase()} long questions`,
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: `Ch ${config.chapterNumber} ${mcqChapter.shortTitle}`, item: mcqChapter.routes.chapterTo },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: `${config.chapterName} Chapter Hub`,
    items: getChapterHubSchemaLinks(props.unit),
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 p-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <h1 class="text-3xl font-bold tracking-tight text-slate-950 dark:text-white">{{ config.heroTitle }}</h1>
        <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
          Chapter {{ config.chapterNumber }} {{ config.chapterName }} ke MCQs, short questions, long questions aur quick preparation links ek jagah.
        </p>

        <div class="mt-5 grid gap-5 xl:grid-cols-[1.15fr_0.85fr]">
          <div class="rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900/40 dark:bg-emerald-950/20">
            <p class="text-sm font-medium text-slate-700 dark:text-slate-300">Exam shortcut:</p>
            <div class="mt-3 space-y-2 text-sm leading-7 text-slate-900 dark:text-slate-100">
              <p v-for="item in config.shortcutItems" :key="item">- {{ item }}</p>
            </div>
          </div>

          <div class="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <p class="text-sm font-medium text-slate-700 dark:text-slate-300">Exam Weight:</p>
            <div class="mt-3 space-y-2 text-sm leading-7 text-slate-900 dark:text-slate-100">
              <p v-for="item in config.examWeight" :key="item">- {{ item }}</p>
            </div>
          </div>
        </div>

        <p class="mt-5 text-base font-semibold text-amber-700">{{ config.heroNote }}</p>
      </section>

      <InteractiveTopics
        v-if="config.interactiveCards?.length"
        eyebrow="Python Chapter 2"
        title="Python Topic Practice"
        intro="Variables, data types, operators, loops, functions aur programs ko topic-wise khol kar concept samjhein aur phir chapter practice karein."
        :cards="config.interactiveCards"
      />

      <section id="what-to-prepare" class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">What To Prepare</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">What to Prepare</h2>
        </div>
        <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Sab se pehle high-priority topics par focus karein, phir medium items cover karein.</p>
        <div class="mt-5 grid gap-4 md:grid-cols-3">
          <article
            v-for="block in config.priorityBlocks"
            :key="block.label"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ block.label }}</h3>
            <div class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
              <p v-for="item in block.items" :key="item">- {{ item }}</p>
            </div>
          </article>
        </div>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Key Topics</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Key Topics (Quick Access)</h2>
        </div>
        <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Jo cheez chahiye us page par seedha jaane ke liye quick links use karein.</p>
        <div class="mt-5 flex flex-wrap gap-3">
          <template v-for="item in keyTopicLinks" :key="item.title">
            <a
              v-if="item.to.startsWith('#')"
              :href="item.to"
              class="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-900 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-slate-900"
            >
              {{ item.title }}
            </a>
            <NuxtLink
              v-else
              :to="item.to"
              class="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-900 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-slate-900"
            >
              {{ item.title }}
            </NuxtLink>
          </template>
        </div>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Practice & Questions</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Practice & Questions</h2>
        </div>
        <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Chapter-wise MCQs, short questions aur long questions se exam preparation complete karein.</p>
        <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <NuxtLink
            v-for="item in config.practiceCards"
            :key="item.to"
            :to="item.to"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
          </NuxtLink>
        </div>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Related (Don't Skip)</h2>
        </div>
        <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Is chapter ko full paper strategy ke sath jorne ke liye yeh pages bhi dekhein.</p>
        <div class="mt-5 grid gap-4 md:grid-cols-3">
          <NuxtLink
            v-for="item in chapterHubRelatedLinks"
            :key="item.to"
            :to="item.to"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
          </NuxtLink>
        </div>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950 md:p-6">
        <p class="text-sm leading-7 text-slate-700 dark:text-slate-300">
          {{ config.seoText }}
        </p>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">
            Chapter {{ config.chapterNumber }} {{ mcqChapter.shortTitle }} FAQs
          </h2>
        </div>
        <div class="mt-5 space-y-4">
          <article
            v-for="faq in faqs"
            :key="faq.question"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
          </article>
        </div>
      </section>
    </div>
  </PageContainer>
</template>
